// The transaction screen's ONE toolbar row, shared by the List, Spread sheet
// and Bulk entry layouts so all three read the same way:
//
//   search scope + its condition chips │ column filters (fold after three,
//   "+N more", Clear all) · + Filter · sort chip · Search in table · density ·
//   (the list layout switch, when there are no tabs)
//
// The scope pieces come from `useEnforcedFilterParts` (the caller owns that
// state, so the criteria panel it returns can sit below this row); the column
// filters / sort / search are the caller's in-memory grid state. Any part a
// layout does not have is simply not passed.

import { useState, type ReactNode } from 'react'
import { ArrowUpDown, Rows3, Rows4, Search, X } from 'lucide-react'
import type { ColumnDef } from '../types'
import type { FilterSpec, SortSpec } from './gridTypes'
import { columnHeading, describeFilter } from './FilterChipBar'
import AddFilterButton from './AddFilterButton'
import { tw } from '../../testHooks'

// Column filters shown before the rest fold into "+N more".
const FILTER_FOLD = 3

export interface ListToolbarProps {
  /** The search scope's button and condition chips (useEnforcedFilterParts). */
  scope?: ReactNode
  chips?: ReactNode
  /** In-memory column filters / sort over the loaded rows. */
  columns?: ColumnDef[]
  columnKeyOf?: (c: ColumnDef) => string
  filters?: FilterSpec[]
  onUpsertFilter?: (f: FilterSpec) => void
  onRemoveFilter?: (column: string) => void
  onClearFilters?: () => void
  sort?: SortSpec[]
  onClearSort?: () => void
  /** Search over the loaded rows. */
  search?: string
  onSearch?: (s: string) => void
  /** Row density (omit to hide the button). */
  dense?: boolean
  onToggleDense?: () => void
  /** The list's own actions (Refresh, Add, ⋮ …), after the search. */
  actions?: ReactNode
  /** Drawn at the end of the row (the list layout switch without tabs). */
  trailing?: ReactNode
}

export default function ListToolbar({
  scope, chips, columns = [], columnKeyOf, filters, onUpsertFilter, onRemoveFilter,
  onClearFilters, sort, onClearSort, search, onSearch, dense, onToggleDense, actions, trailing,
}: ListToolbarProps) {
  const [expanded, setExpanded] = useState(false)
  const keyOf = columnKeyOf || ((c: ColumnDef) => String(c.name))
  const all = filters || []
  const shown = expanded ? all : all.slice(0, FILTER_FOLD)
  const hidden = all.length - shown.length
  const canFilter = !!(filters && onUpsertFilter && onRemoveFilter)
  return (
    <div style={rowStyle}>
      {scope}
      {chips}
      {(scope || chips) && <span aria-hidden style={dividerStyle} />}
      <div style={{ flex: '1 1 120px', minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}
           title="Column filters narrow the rows already loaded; they don't change which records are fetched">
        {canFilter && (
          <>
            {shown.map((f) => {
              const head = columnHeading(columns, keyOf, f.column)
              return (
                <span key={f.column} style={filterChipStyle} {...tw('action', `filter-chip:${f.column}`)}>
                  <strong style={{ fontWeight: 600 }}>{head}</strong> {describeFilter(f)}
                  <button type="button" onClick={() => onRemoveFilter!(f.column)}
                          aria-label={`Remove filter on ${head}`} title="Remove filter" style={chipXStyle}>
                    <X size={12} />
                  </button>
                </span>
              )
            })}
            {(hidden > 0 || (expanded && all.length > FILTER_FOLD)) && (
              <button type="button" onClick={() => setExpanded((e) => !e)}
                      aria-expanded={expanded} {...tw('action', 'filter-more')}
                      style={{ ...filterChipStyle, padding: '0 12px', fontWeight: 600, cursor: 'pointer',
                        background: 'var(--color-surface-2, #f0f3f7)', color: 'var(--color-text-secondary, #465062)',
                        borderColor: 'var(--color-border, #dde3ea)' }}>
                {expanded ? 'Show fewer' : `+${hidden} more`}
              </button>
            )}
            <AddFilterButton columns={columns} columnKeyOf={keyOf} filters={all}
                             onUpsert={onUpsertFilter!} onRemove={onRemoveFilter!} variant="chip" />
            {all.length > 1 && onClearFilters && (
              <button type="button" onClick={onClearFilters} {...tw('action', 'filter-clear')}
                      style={linkStyle}>
                Clear all
              </button>
            )}
          </>
        )}
        {sort && sort.length > 0 && onClearSort && (
          <span style={sortChipStyle}>
            <ArrowUpDown size={13} aria-hidden="true" />
            Sorted by {sort.map((so, i) => (
              <span key={so.column}>
                {i > 0 && ', '}{columnHeading(columns, keyOf, so.column)} {so.dir === 'asc' ? '↑' : '↓'}
              </span>
            ))}
            <button type="button" onClick={onClearSort} aria-label="Clear sort" title="Clear sort"
                    {...tw('action', 'sort-clear')} style={chipXStyle}>
              <X size={12} />
            </button>
          </span>
        )}
      </div>
      {/* Search, density and the list's actions are one group: they wrap
          together, never Refresh / ⋮ alone onto a line of their own. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', flex: '0 1 auto', minWidth: 0 }}>
      {onSearch && (
        <label style={{ position: 'relative', flex: '0 1 240px', width: 240, minWidth: 140, display: 'block' }}>
          <span aria-hidden style={{ position: 'absolute', left: 11, top: 9, color: 'var(--color-text-faint, #687281)', display: 'flex' }}>
            <Search size={17} />
          </span>
          <input
            type="search"
            data-grid-search
            {...tw('field', '_search')}
            value={search || ''}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search in table"
            aria-label="Search in table"
            title="Searches the rows already loaded"
            style={searchStyle}
          />
        </label>
      )}
      {onToggleDense && (
        <button type="button" onClick={onToggleDense} aria-pressed={!!dense} aria-label="Row density"
                title={dense ? 'Comfortable rows' : 'Compact rows'} {...tw('action', 'density')}
                style={iconBtnStyle}>
          {dense ? <Rows3 size={17} /> : <Rows4 size={17} />}
        </button>
      )}
      {actions}
      </div>
      {trailing}
    </div>
  )
}

const rowStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
  padding: '12px 16px 12px 20px', borderBottom: '1px solid var(--color-border, #dde3ea)',
  background: 'var(--color-bg-secondary, #ffffff)',
}
const dividerStyle: React.CSSProperties = {
  width: 1, height: 24, background: 'var(--color-border, #dde3ea)', margin: '0 2px', flex: 'none',
}
const filterChipStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, height: 32, padding: '0 6px 0 12px',
  borderRadius: 999, border: '1px solid var(--color-border-strong, #c7cfd9)',
  background: 'var(--color-bg-secondary, #ffffff)', color: 'var(--color-text, #1c2430)',
  fontSize: '0.8125rem', whiteSpace: 'nowrap', fontFamily: 'inherit',
}
const sortChipStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 6px 0 10px',
  borderRadius: 999, background: 'var(--color-surface-2, #f0f3f7)',
  color: 'var(--color-text-secondary, #465062)', fontSize: '0.8125rem', whiteSpace: 'nowrap',
}
const chipXStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22,
  border: 0, borderRadius: '50%', background: 'transparent',
  color: 'var(--color-text-secondary, #465062)', padding: 0, cursor: 'pointer',
}
const linkStyle: React.CSSProperties = {
  background: 'none', border: 0, padding: 0, marginLeft: 2, color: 'var(--color-accent, #0061D0)',
  fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
}
const searchStyle: React.CSSProperties = {
  boxSizing: 'border-box', width: '100%', height: 36, padding: '0 12px 0 36px',
  border: '1px solid var(--color-border-strong, #c7cfd9)', borderRadius: 8,
  background: 'var(--color-input-bg, #ffffff)', color: 'var(--color-text, #1c2430)',
  fontSize: '0.875rem', fontFamily: 'inherit',
}
export const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36,
  borderRadius: 8, border: '1px solid transparent', background: 'transparent',
  color: 'var(--color-text-secondary, #465062)', cursor: 'pointer', flex: 'none', padding: 0,
}
